import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/Button";
import { LanguageSwitcher } from "../ui/LanguageSwitcher";
import { useI18n } from "../../i18n/useI18n";
import { useAuth } from "../../lib/auth";
import { scrollToSection } from "../../lib/scroll-to-section";

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useI18n();
  const { session, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const portalHref = profile ? (profile.role === "KUNDE" ? "/kundenbereich" : "/admin") : "/login";
  const portalLabel = session ? "Mein Portal" : "Login";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile menu overlay is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  // In-page section links (#compare, #why, #faq, #contact, #top) go through
  // Lenis's own scrollTo instead of native anchor jumps, and first navigate
  // to "/" when the target section only lives on the Home page. Fixes
  // FAQ/Kontakt (and siblings) not reacting to clicks from any route.
  const goToSection = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    closeMenu();
    scrollToSection(sectionId, navigate, location.pathname);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-30 transition-all duration-500 ease-lux ${
        scrolled || menuOpen ? "border-b border-slate-200/80 bg-pearl/80 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="container-lux flex h-[72px] items-center gap-6">
        <Link to="/" className="font-display text-xl" onClick={closeMenu} title="Jyotish Astro — Startseite">
          Jyotish<span className="text-gold">Astro</span>
        </Link>

        <nav className="ml-auto hidden items-center gap-7 text-sm text-slate2/80 md:flex">
          <a href="/#compare" onClick={goToSection("compare")} className="hover:text-gold" title="Siderischer vs. tropischer Tierkreis im Vergleich">{t.nav.compare}</a>
          <a href="/#why" onClick={goToSection("why")} className="hover:text-gold" title="Die 3 Säulen der vedischen Astrologie">{t.nav.why}</a>
          <Link to="/nakshatras-und-dasha" className="hover:text-gold" title="Nakshatras & Vimshottari Dasha erklärt">Nakshatras &amp; Dasha</Link>
          <Link to="/angebote" className="hover:text-gold" title="Angebote & Pakete für deine Jyotish-Beratung">Angebote</Link>
          <Link to="/beratung" className="hover:text-gold" title="Persönliche 1-on-1 Jyotish-Beratung buchen">Beratung</Link>
          <a href="/#faq" onClick={goToSection("faq")} className="hover:text-gold" title="Häufige Fragen zur vedischen Astrologie">{t.nav.faq}</a>
          <a href="/#contact" onClick={goToSection("contact")} className="hover:text-gold" title="Kontakt aufnehmen">{t.contact.eyebrow}</a>
        </nav>

        <div className="ml-auto flex items-center gap-3 md:ml-0">
          <LanguageSwitcher />
          <Link to={portalHref} className="hidden text-sm text-slate2/80 hover:text-gold md:inline">
            {portalLabel}
          </Link>
          <a href="/#top" onClick={goToSection("top")} className="hidden sm:inline-flex">
            <Button className="!px-5 !py-2.5">{t.nav.cta}</Button>
          </a>
          <button
            type="button"
            aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/80 bg-white/70 text-charcoal md:hidden"
          >
            <span className="relative block h-3.5 w-4">
              <span
                className={`absolute left-0 top-0 h-0.5 w-full rounded-full bg-current transition-transform duration-300 ${
                  menuOpen ? "translate-y-[6px] rotate-45" : ""
                }`}
              />
              <span
                className={`absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 rounded-full bg-current transition-opacity duration-200 ${
                  menuOpen ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-current transition-transform duration-300 ${
                  menuOpen ? "-translate-y-[6px] -rotate-45" : ""
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-slate-200/80 bg-pearl/95 px-6 pb-8 pt-4 backdrop-blur-xl md:hidden">
          <nav className="flex flex-col gap-1 text-base text-slate2/80">
            <a href="/#compare" onClick={goToSection("compare")} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">{t.nav.compare}</a>
            <a href="/#why" onClick={goToSection("why")} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">{t.nav.why}</a>
            <Link to="/nakshatras-und-dasha" onClick={closeMenu} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">Nakshatras &amp; Dasha</Link>
            <Link to="/angebote" onClick={closeMenu} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">Angebote</Link>
            <Link to="/beratung" onClick={closeMenu} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">Beratung</Link>
            <a href="/#faq" onClick={goToSection("faq")} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">{t.nav.faq}</a>
            <a href="/#contact" onClick={goToSection("contact")} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">{t.contact.eyebrow}</a>
            <Link to="/vedischer-rechner" onClick={closeMenu} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">Vedischer Rechner</Link>
            <Link to="/info" onClick={closeMenu} className="rounded-xl px-3 py-3 hover:bg-white/70 hover:text-gold">Über uns / Impressum</Link>
            <Link to={portalHref} onClick={closeMenu} className="rounded-xl px-3 py-3 font-medium text-gold-deep hover:bg-white/70">{portalLabel}</Link>
          </nav>
          <a href="/#top" onClick={goToSection("top")} className="mt-4 block">
            <Button className="w-full">{t.nav.cta}</Button>
          </a>
        </div>
      )}
    </header>
  );
}
